import { Context, Deferred, Effect, Layer, Ref, Scope } from "effect";

interface Completion {
  readonly done: Deferred.Deferred<void>;
}
interface OrderState {
  readonly tails: ReadonlyMap<string, Completion>;
  readonly pending: number;
}
export interface PersistenceOrderShape {
  readonly run: <A, E, R>(key: string, operation: Effect.Effect<A, E, R>) => Effect.Effect<A, E, Exclude<R, Scope.Scope>>;
  readonly inspect: Effect.Effect<{ readonly lanes: number; readonly pending: number }>;
  readonly contains: (key: string) => Effect.Effect<boolean>;
}
export class PersistenceOrder extends Context.Service<PersistenceOrder, PersistenceOrderShape>()("workerstation/frontend/StationPersistenceOrder") {}

/** Enroll only within AcceptedWork.join. A queued mutation keeps its captured
 * target and owns its complete result/cleanup even after its view disappears. */
export const makePersistenceOrder = Effect.fn("Frontend.StationPersistenceOrder.make")(function* () {
  const state = yield* Ref.make<OrderState>({ tails: new Map(), pending: 0 });
  const run = Effect.fn("Frontend.StationPersistenceOrder.run")(<A, E, R>(key: string, operation: Effect.Effect<A, E, R>): Effect.Effect<A, E, Exclude<R, Scope.Scope>> => Effect.uninterruptible(Effect.gen(function* () {
    const completion: Completion = { done: yield* Deferred.make<void>() };
    const previous = yield* Ref.modify(state, current => [current.tails.get(key), { tails: new Map(current.tails).set(key, completion), pending: current.pending + 1 }] as const);
    const release = Effect.gen(function* () {
      yield* Ref.update(state, current => {
        const tails = new Map(current.tails);
        if (tails.get(key) === completion) tails.delete(key);
        return { tails, pending: current.pending - 1 };
      });
      yield* Deferred.succeed(completion.done, undefined);
    });
    return yield* Effect.gen(function* () {
      if (previous) yield* Deferred.await(previous.done);
      return yield* Effect.scoped(operation);
    }).pipe(Effect.ensuring(release));
  })));
  return PersistenceOrder.of({ run, inspect: Ref.get(state).pipe(Effect.map(current => ({ lanes: current.tails.size, pending: current.pending }))),
    contains: key => Ref.get(state).pipe(Effect.map(current => current.tails.has(key))) });
});
export const PersistenceOrderLive = Layer.effect(PersistenceOrder, makePersistenceOrder());
